// A media element's playback state as refs, and the actions on it, for every set of
// controls (MediaControls, the play bar, Now Playing). They act on the element itself, so
// anything listening to its events (watch party sync) sees the same play/pause/seeked events.

import { computed, onBeforeUnmount, ref, watch } from 'vue'

export function useMediaState(el: () => HTMLMediaElement | null) {
  const playing = ref(false)
  const time = ref(0)
  const duration = ref(NaN)
  const buffered = ref(0)
  const volume = ref(1)
  const muted = ref(false)
  /** Where the seek bar is being dragged to, before letting go. */
  const seeking = ref<number | null>(null)

  const known = computed(() => Number.isFinite(duration.value) && duration.value > 0)
  const shown = computed(() => seeking.value ?? time.value)
  const pct = (t: number) => (known.value ? `${Math.min(100, (t / duration.value) * 100)}%` : '0%')

  function sync(): void {
    const m = el()
    if (!m) return
    playing.value = !m.paused && !m.ended
    time.value = m.currentTime
    duration.value = m.duration
    volume.value = m.volume
    muted.value = m.muted
    const b = m.buffered
    buffered.value = b.length ? b.end(b.length - 1) : 0
  }

  const EVENTS = ['play', 'pause', 'ended', 'timeupdate', 'durationchange', 'loadedmetadata', 'progress', 'volumechange', 'seeked', 'emptied']
  let bound: HTMLMediaElement | null = null
  function bind(m: HTMLMediaElement | null): void {
    for (const e of EVENTS) bound?.removeEventListener(e, sync)
    bound = m
    for (const e of EVENTS) m?.addEventListener(e, sync)
    sync()
  }
  watch(el, bind, { immediate: true })
  onBeforeUnmount(() => bind(null))

  function toggle(): void {
    const m = el()
    if (!m) return
    if (m.paused || m.ended) void m.play().catch(() => undefined)
    else m.pause()
  }

  /** The seek bar moving (shown, not yet applied). */
  function scrub(e: Event): void {
    seeking.value = Number((e.target as HTMLInputElement).value)
  }
  /** The seek bar let go: jump there. */
  function seek(e: Event): void {
    const m = el()
    const t = Number((e.target as HTMLInputElement).value)
    seeking.value = null
    if (m && Number.isFinite(t)) m.currentTime = t
  }

  function setVolume(e: Event): void {
    const m = el()
    if (!m) return
    m.volume = Number((e.target as HTMLInputElement).value)
    m.muted = m.volume === 0
  }
  function toggleMute(): void {
    const m = el()
    if (!m) return
    m.muted = !m.muted
    if (!m.muted && m.volume === 0) m.volume = 0.5
  }

  return { playing, time, duration, buffered, volume, muted, seeking, known, shown, pct, toggle, scrub, seek, setVolume, toggleMute }
}

